/**
 * PartnerPayoutsTab — admin mirror of fleet-partner's Payouts.tsx.
 *
 * Table columns match fleet-partner: Week | Vehicles | Earnings | Payout | Payout Date | Status
 * Stat card order: Total Expected → Payouts Received → Outstanding → Collection Rate → Earning Vehicles
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ListPagination } from '@/components/ListPagination';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { Search, Calendar } from 'lucide-react';
import { startOfMonth, subMonths, endOfMonth, format, getISOWeek } from 'date-fns';
import {
    businessDayEndInstant, businessDayStartInstant, businessToday, businessWeekBounds,
    formatBusinessDate, formatCurrency,
} from '@/lib/format';

interface Props {
    partnerId: number;
}

interface WeeklyPayoutRow {
    week_start: string;
    due_date: string;
    vehicle_count: number;
    total_repayments: number;
    total_partner_amount: number;
    payout_amount: number;
    payout_id: number | null;
    payout_transaction_id: number | null;
    payout_created_at: string | null;
    payout_description: string | null;
    status: 'paid' | 'partial' | 'due' | 'upcoming';
    repayment_ids: number[];
}

const PAGE_SIZE = 15;

export default function PartnerPayoutsTab({ partnerId }: Props) {
    const navigate = useNavigate();

    // Business-calendar months; the RPC bounds are built from their parts, never toISOString().
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });
    const fromInstant = businessDayStartInstant(monthRange.from);
    const toInstant = businessDayEndInstant(monthRange.to);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => { setDebouncedSearch(search); setCurrentPage(1); }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Payout summary for stat cards
    const { data: payoutSummary, isLoading: summaryLoading } = useQuery({
        queryKey: ['partner-payout-summary-admin', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_payout_summary', {
                p_partner_id: partnerId,
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) throw error;
            return data?.[0] as {
                total_expected_all_time: number;
                total_received_all_time: number;
                outstanding_payouts: number;
                weeks_with_repayments: number;
                avg_weekly_partner_portion: number;
                earning_vehicles: number;
                collection_rate: number;
            } | null;
        },
        enabled: !!partnerId,
    });

    // Weekly payouts table
    const { data: weeklyPayouts, isLoading } = useQuery({
        queryKey: ['partner-weekly-payouts-admin', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_weekly_payouts', {
                p_partner_id: partnerId,
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) throw error;
            return (data as WeeklyPayoutRow[]) || [];
        },
        enabled: !!partnerId,
    });

    // Filter + paginate
    const filtered = useMemo(() => {
        if (!weeklyPayouts) return [];
        if (!debouncedSearch) return weeklyPayouts;
        const q = debouncedSearch.toLowerCase();
        return weeklyPayouts.filter(w => {
            const week = businessWeekBounds(w.week_start);
            const weekLabel = format(week.weekStart, 'MMM d') + ' – ' + format(week.weekEnd, 'MMM d, yyyy');
            return weekLabel.toLowerCase().includes(q)
                || w.status.toLowerCase().includes(q);
        });
    }, [weeklyPayouts, debouncedSearch]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginatedWeeks = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    useEffect(() => { setCurrentPage(1); }, [monthRange.from, monthRange.to]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'paid':
                return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
            case 'partial':
                return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Partial</Badge>;
            case 'due':
                return <Badge className="bg-red-100 text-red-700 border-red-200">No payments</Badge>;
            case 'upcoming':
                return <Badge variant="outline" className="text-muted-foreground">Upcoming</Badge>;
            default:
                return null;
        }
    };

    const navigateToWeek = (weekKey: string) => {
        const params = new URLSearchParams();
        params.set('week', weekKey);
        params.set('partner', String(partnerId));
        navigate(`/dashboard/partners/payouts/week?${params.toString()}`);
    };

    return (
        <div className="space-y-6">
            {/* Date Picker */}
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* Stat Cards */}
            {summaryLoading ? (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
            ) : (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    <StatCard title="Total Expected" value={<CurrencyDisplay amount={payoutSummary?.total_expected_all_time ?? 0} />} description="All-time earnings" />
                    <StatCard title="Payouts Received" value={<CurrencyDisplay amount={payoutSummary?.total_received_all_time ?? 0} />} description="Total paid out" />
                    <StatCard title="Outstanding" value={<CurrencyDisplay amount={payoutSummary?.outstanding_payouts ?? 0} />} description="Awaiting payout" />
                    <StatCard title="Collection Rate" value={`${(payoutSummary?.collection_rate ?? 0).toFixed(2)}%`} description="Of expected received" />
                    <StatCard title="Earning Vehicles" value={payoutSummary?.earning_vehicles ?? 0} description="Vehicles generating income" />
                </div>
            )}

            {/* Weekly Payouts Table */}
            <Card className="border-0">
                <CardContent className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="Search by week..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                        </div>
                            <ListPagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} onPageChange={setCurrentPage} />
                    </div>

                    {isLoading ? (
                        <Skeleton className="h-48 w-full" />
                    ) : paginatedWeeks.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[220px]">Week</TableHead>
                                        <TableHead>Vehicles</TableHead>
                                        <TableHead>Earnings</TableHead>
                                        <TableHead>Payout</TableHead>
                                        <TableHead>Payout Date</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedWeeks.map((week) => {
                                        const { weekStart, weekEnd, weekStartYmd: weekKey } = businessWeekBounds(week.week_start);
                                        return (
                                            <TableRow
                                                key={weekKey}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => navigateToWeek(weekKey)}
                                            >
                                                <TableCell className="whitespace-nowrap">
                                                    <div className="font-medium">{format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}</div>
                                                    <div className="text-xs text-muted-foreground">Week {getISOWeek(weekStart)}</div>
                                                </TableCell>
                                                <TableCell>{week.vehicle_count}</TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {formatCurrency(week.total_partner_amount)}
                                                </TableCell>
                                                <TableCell className="font-medium">
                                                    {week.payout_amount > 0
                                                        ? formatCurrency(week.payout_amount)
                                                        : <span className="text-muted-foreground font-normal">—</span>
                                                    }
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {week.payout_created_at
                                                        ? formatBusinessDate(week.payout_created_at)
                                                        : '—'}
                                                </TableCell>
                                                <TableCell>{getStatusBadge(week.status)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="py-16 text-center text-muted-foreground flex flex-col items-center gap-2">
                            <Calendar className="h-8 w-8 text-muted-foreground/40" />
                            <p>No payout data available for the selected period.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
